import { type AABB2, View } from "@novorender/api";
import { BoundingSphere } from "@novorender/data-js-api";
import { glMatrix, vec2, vec3 } from "gl-matrix";
import { t } from "i18next";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useHighlighted } from "contexts/highlighted";
import { getCameraDir } from "features/engine2D/utils";
import { imagesActions } from "features/images";
import { CameraType, renderActions, selectCameraDefaults, selectCameraType, selectViewMode } from "features/render";
import { selectTopLevelSelections } from "features/render/renderSlice";
import { explorerActions, selectIsOnline } from "slices/explorer";
import { ViewMode } from "types/misc";
import { isRectInsideCircle, pointToPlaneDistance, pointToRectDistance } from "utils/math";
import { getSelectionBoundingSphere, resolveTopLevelBoundingSphere } from "utils/objectData";
import { sleep } from "utils/time";

enum Status {
    Initial,
    Loading,
}

// Padding to add around object when flying to it in orthographic view
// 0.2 is 20% of bounding sphere diameter (or bounding rect max dimension)
const ORTHO_PADDING = 0.2;

const MIN_HIGHLIGHTED_IDS_FOR_FAST_PATH = 5000;

export function useFlyToSelected() {
    const {
        state: { dataAccess, scene, view },
    } = useExplorerGlobals();
    const dispatch = useAppDispatch();
    const isOnline = useAppSelector(selectIsOnline);
    const highlighted = useHighlighted().idArr;
    const viewMode = useAppSelector(selectViewMode);
    const isCrossSection = [ViewMode.CrossSection, ViewMode.FollowPath, ViewMode.Deviations].includes(viewMode);
    const cameraType = useAppSelector(selectCameraType);
    const cameraDefaults = useAppSelector(selectCameraDefaults);
    const topLevelSelections = useAppSelector(selectTopLevelSelections);

    const orthoLoadingHandle = useRef(0);
    const [orthoLoading, setOrthoLoading] = useState(false);
    const [status, setStatus] = useState(Status.Initial);
    const abortController = useRef(new AbortController());

    const previousBoundingSphere = useRef<BoundingSphere>();

    useEffect(() => {
        previousBoundingSphere.current = undefined;
        abortController.current.abort();
        abortController.current = new AbortController();
        setStatus(Status.Initial);
    }, [highlighted, setStatus]);

    const defaultShowTooltip = useCallback(
        (msg: string) => {
            dispatch(explorerActions.setSnackbarMessage({ msg, closeAfter: 3000 }));
        },
        [dispatch],
    );

    const flyToSelected = useCallback(
        async ({ showTooltip = defaultShowTooltip }: { showTooltip?: (msg: string) => void } = {}) => {
            abortController.current.abort();
            abortController.current = new AbortController();

            if (!highlighted.length || !view || !dataAccess || !scene) {
                return;
            }

            if (orthoLoadingHandle.current) {
                setOrthoLoading(false);
                orthoLoadingHandle.current = 0;
            }

            const lookAtSphereInOrtho = (sphere: BoundingSphere) => {
                const cameraDir = getCameraDir(view.renderState.camera.rotation);
                const pinholeFov = view.controllers.flight.fov;
                const dist = Math.max(sphere.radius / Math.tan(glMatrix.toRadian(pinholeFov) / 2), sphere.radius);
                const offset = -Math.min(dist, view.renderState.camera.far * 0.9);
                const position = vec3.scaleAndAdd(vec3.create(), sphere.center, cameraDir, offset);

                dispatch(
                    renderActions.setCamera({
                        type: CameraType.Orthographic,
                        goTo: {
                            position,
                            rotation: view.renderState.camera.rotation,
                            fov: sphere.radius * 2 * (1 + ORTHO_PADDING),
                        },
                    }),
                );
            };

            const goToOrtho = async (sphere: BoundingSphere) => {
                if (isCrossSection && view.renderState.clipping.planes.length > 0) {
                    // In cross section bounding sphere might differ from the visible object part a lot,
                    // so we try to focus on the outline instead of bounding sphere.
                    // If object far outside the view (based on the bounding sphere) - focus bounding sphere first
                    // and then after 1 second (so outlines are generated) - focus on the outlines.
                    const plane = view.renderState.clipping.planes[0];
                    const planeToSphereDist = pointToPlaneDistance(sphere.center, plane.normalOffset);
                    if (planeToSphereDist > sphere.radius + view.renderState.camera.far) {
                        showTooltip("Object is outside the current cross section view");
                        return;
                    }

                    const width = window.innerWidth;
                    const height = window.innerHeight;
                    const screenRect = {
                        min: vec2.fromValues(0, 0),
                        max: vec2.fromValues(width, height),
                    };

                    const [sphereCenter2d] = view.convert
                        .worldSpaceToViewSpace([sphere.center])
                        .map((p) => vec2.fromValues(p[0] * width, p[1] * height));

                    let visibleRadiusRatio = 0;
                    if (sphereCenter2d) {
                        const sphereRadius2d = (height / view.renderState.camera.fov) * sphere.radius;
                        const isSphereCenterInsideScreenRect =
                            sphereCenter2d[0] >= 0 &&
                            sphereCenter2d[0] <= width &&
                            sphereCenter2d[1] >= 0 &&
                            sphereCenter2d[1] <= height;

                        if (
                            isSphereCenterInsideScreenRect ||
                            isRectInsideCircle(screenRect, sphereCenter2d, sphereRadius2d)
                        ) {
                            visibleRadiusRatio = 1;
                        } else {
                            const sphereToRectDist = pointToRectDistance(sphereCenter2d, screenRect);
                            visibleRadiusRatio = Math.max(0, (sphereRadius2d - sphereToRectDist) / sphereRadius2d);
                        }
                    }

                    let loading = 0;
                    if (visibleRadiusRatio < 0.5) {
                        orthoLoadingHandle.current = loading = performance.now();
                        setOrthoLoading(true);
                        lookAtSphereInOrtho(sphere);

                        // Wait for camera to move and outlines to be generated
                        await sleep(1000);
                    }

                    if (loading) {
                        if (loading !== orthoLoadingHandle.current) {
                            return;
                        } else {
                            orthoLoadingHandle.current = 0;
                            setOrthoLoading(false);
                        }
                    }

                    const zoomingParams = getSelectedObjectsOutlineZoomingParams(view, new Set(highlighted));
                    if (zoomingParams) {
                        dispatch(
                            renderActions.setCamera({
                                type: CameraType.Orthographic,
                                goTo: {
                                    position: zoomingParams.position,
                                    rotation: view.renderState.camera.rotation,
                                    fov: zoomingParams.fov * (1 + ORTHO_PADDING),
                                },
                            }),
                        );
                    }
                } else {
                    lookAtSphereInOrtho(sphere);
                }
            };

            const goTo = (sphere: BoundingSphere) => {
                if (cameraType === CameraType.Pinhole) {
                    dispatch(renderActions.setCamera({ type: CameraType.Pinhole, zoomTo: sphere }));

                    // dist formula is copied from novorender/api zoomTo method
                    const fov = view.controllers[cameraDefaults.pinhole.controller].fov;
                    const dist = Math.max(sphere.radius / Math.tan(glMatrix.toRadian(fov) / 2), sphere.radius);
                    if (dist * 1.1 > cameraDefaults.pinhole.clipping.far) {
                        dispatch(explorerActions.setCameraIsTooFar("flyTo"));
                    }
                } else {
                    goToOrtho(sphere);
                }
                dispatch(imagesActions.setActiveImage(undefined));
            };

            if (previousBoundingSphere.current) {
                goTo(previousBoundingSphere.current);
                return;
            }

            setStatus(Status.Loading);

            try {
                const abortSignal = abortController.current.signal;

                if (!isOnline && !dataAccess.isDataAvailableOffline()) {
                    showTooltip(t("notAvailableOffline"));
                    return;
                }

                const flipCoords = !vec3.equals(scene.up ?? [0, 1, 0], [0, 0, 1]);

                // Fast path: use pre-derived top-level selections when available.
                // resolveTopLevelBoundingSphere fetches one object per root node
                // instead of batchedPropertySearch over all highlighted IDs.
                const topLevelEntryCount = Object.keys(topLevelSelections).length;
                if (
                    topLevelEntryCount > 0 &&
                    highlighted.length >= MIN_HIGHLIGHTED_IDS_FOR_FAST_PATH &&
                    (isOnline || (dataAccess.isIndexAvailableOffline() && dataAccess.isDataAvailableOffline()))
                ) {
                    const fastSphere = await resolveTopLevelBoundingSphere({
                        topLevelSelections,
                        dataAccess,
                        abortSignal,
                        flipCoords,
                    });
                    if (fastSphere) {
                        previousBoundingSphere.current = fastSphere;
                        goTo(fastSphere);
                        return;
                    }
                    // Fast path found no spheres — fall through to the full selection.
                }

                const boundingSphere = await getSelectionBoundingSphere({
                    ids: highlighted,
                    dataAccess,
                    abortSignal,
                    flipCoords,
                });

                if (boundingSphere) {
                    previousBoundingSphere.current = boundingSphere;
                    goTo(boundingSphere);
                } else {
                    dispatch(
                        explorerActions.setSnackbarMessage({
                            msg: t(abortSignal.aborted ? "flyToSelectionCancelled" : "nothingToFlyTo"),
                            closeAfter: 3000,
                        }),
                    );
                }
            } finally {
                setStatus(Status.Initial);
            }
        },
        [
            highlighted,
            view,
            cameraType,
            cameraDefaults,
            dispatch,
            isCrossSection,
            dataAccess,
            scene,
            defaultShowTooltip,
            isOnline,
            topLevelSelections,
        ],
    );

    return {
        flyToSelected,
        orthoLoading,
        isLoading: status === Status.Loading,
        disabled: !highlighted.length || (!isOnline && !dataAccess?.isDataAvailableOffline()),
    } as const;
}

function getSelectedObjectsOutlineZoomingParams(view: View, objectIds: Set<number>) {
    const box = view.getObjectsOutlinePlaneBoundingRectInWorld("clipping", 0, objectIds);
    if (!box) {
        return;
    }

    const width = window.innerWidth;
    const height = window.innerHeight;

    const center3d = vec3.lerp(vec3.create(), box.min, box.max, 0.5);
    const [screenMin, screenMax] = view.convert
        .worldSpaceToViewSpace([box.min, box.max])
        .map((p) => vec2.fromValues(p[0] * width, p[1] * height));

    const fovK = getOrthoFovRatioForAABB2(width, height, { min: screenMin, max: screenMax });
    if (!fovK) {
        return;
    }

    return {
        position: center3d,
        fov: view.renderState.camera.fov * fovK,
    };
}

function getOrthoFovRatioForAABB2(screenWidth: number, screenHeight: number, bbox: AABB2): number {
    const width = Math.abs(bbox.max[0] - bbox.min[0]);
    const height = Math.abs(bbox.max[1] - bbox.min[1]);
    const kHeight = screenHeight / height;
    const kWidth = screenWidth / width;
    const k = Math.min(kHeight, kWidth);
    return 1 / k;
}
